import {Box, IconButton, Typography} from "@mui/material";
import React from "react";
import {Close, Delete} from "@mui/icons-material";
import Chip from "@mui/material/Chip";
import {DateTime} from "luxon";
import {
    FilterAction,
    FilterState,
    FilterOption,
    IncludeExcludeFilterCategory,
} from "../reducers/includeExcludeFilterReducer";
import {DateRange} from "@mui/x-date-pickers-pro";
import {
    getDateOperatorDisplayText,
    hasDateOperatorValues,
} from "../../../utils/filterDateOperatorUtils";
import {
    getNumberRangeDisplayText,
    hasNumberRangeValues,
} from "../../../utils/filterNumberRangeUtils";
import {findFilterOption} from "../findFilterOption";

const formatDate = (dateTime: string | DateTime) => {
    return DateTime.fromISO(dateTime as string, {zone: "utc"}).toFormat(
        "M/d/yyyy",
    );
};

const buildCategoryOrder = (
  categories: IncludeExcludeFilterCategory[],
): Map<string, number> => {
  const categoryOrder = new Map<string, number>();

  const visitOptions = (options: FilterOption[]) => {
    options.forEach((option) => {
      if (!categoryOrder.has(option.categoryId)) {
        categoryOrder.set(option.categoryId, categoryOrder.size);
      }

      if (option.subItems?.length) {
        visitOptions(option.subItems);
      }
    });
  };

  categories.forEach((category) => {
    if (!categoryOrder.has(category.id)) {
      categoryOrder.set(category.id, categoryOrder.size);
    }
    visitOptions(category.options);
  });

  return categoryOrder;
};

export const FilterSection = ({
                                  type,
                                  icon,
                                  label,
                                  iconColor,
                                  getCategoryLabel,
                                  validCategories,
                                  filterSelectionState,
                                  dispatch,
                                  onCategoryClear,
                              }: {
    type: "include" | "exclude";
    icon: React.ReactNode;
    label: string;
    iconColor: string;
    validCategories: IncludeExcludeFilterCategory[];
    getCategoryLabel: (categoryId: string) => string;
    filterSelectionState: FilterState;
    dispatch: React.Dispatch<FilterAction>;
    onCategoryClear?: (categoryId: string, filterType: "include" | "exclude") => void;
}) => {
    const filterData = filterSelectionState.filters[type];
    const hasFilters =
        Object.keys(filterData).length > 0 &&
        Object.values(filterData).some((arr) => arr?.length > 0);

    if (!hasFilters) return null;

    const categoryOrder = buildCategoryOrder(validCategories);

    const getOptionLabel = (
        categoryType: "checkbox" | "date-range" | "date-operator" | undefined,
        optionId: string | string[] | DateRange<DateTime>,
    ): string | string[] => {
        if (categoryType === "date-range" && optionId[0] && optionId[1]) {
            const startDate = formatDate(optionId[0]);
            const endDate = formatDate(optionId[1]);
            return `${startDate} - ${endDate}`;
        }
        return optionId as string | string[];
    };

    const handleClearFilters = (categoryId: string) => {
        dispatch({
            type: "CLEAR_FILTER_TYPE",
            payload: {filterType: type, categoryId: categoryId},
        });
        onCategoryClear?.(categoryId, type);
    };

        const sortedFilterEntries = Object.entries(filterData).sort(
          ([leftId], [rightId]) => {
            const leftOrder =
              categoryOrder.get(leftId) ?? Number.MAX_SAFE_INTEGER;
            const rightOrder =
              categoryOrder.get(rightId) ?? Number.MAX_SAFE_INTEGER;

            return leftOrder - rightOrder || leftId.localeCompare(rightId);
          },
        );

    return (
        <>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{display: "flex", alignItems: "center", p: 2, pb: 1}}
            >
                {React.cloneElement(icon as React.ReactElement<{
                    fontSize?: string;
                    sx?: {marginRight?: number; color?: string};
                }>, {
                    fontSize: "inherit",
                    sx: {marginRight: 1, color: iconColor},
                })}{" "}
                {label}:
            </Typography>
            {sortedFilterEntries.map(([categoryId, selectedOptions]) => {
                if (!selectedOptions || selectedOptions.length === 0) return null;

                const findTopLevelCategoryIterative = (
                    categories: IncludeExcludeFilterCategory[],
                    categoryId: string,
                ): IncludeExcludeFilterCategory | undefined => {
                    const stack: IncludeExcludeFilterCategory[] = [...categories];

                    while (stack.length > 0) {
                        const category = stack.pop();
                        if (!category) continue;

                        if (category.id === categoryId) {
                            return category;
                        }

                        for (const option of category.options) {
                            if (option.categoryId === categoryId) {
                                return category;
                            }
                            if (option.subItems && option.subItems.length > 0) {
                                stack.push({
                                    id: option.categoryId,
                                    label: option.label || "",
                                    options: option.subItems,
                                });
                            }
                        }
                    }

                    return undefined;
                };

                const category = findTopLevelCategoryIterative(
                    validCategories,
                    categoryId,
                );

                const countOptionsForCategoryId = (
                    options: FilterOption[],
                    targetCategoryId: string,
                ): number => {
                    return options.reduce((count, opt) => {
                        if (opt.categoryId === targetCategoryId) {
                            count += 1;
                        }
                        if (opt.subItems && opt.subItems.length > 0) {
                            count += countOptionsForCategoryId(opt.subItems, targetCategoryId);
                        }
                        return count;
                    }, 0);
                };
                const totalOptions = validCategories.reduce((count, validCategory) => {
                    return count + countOptionsForCategoryId(validCategory.options, categoryId);
                }, 0);

                const isAllSelected =
                    selectedOptions.length === totalOptions && totalOptions > 1;
                const categoryLabel = getCategoryLabel(categoryId);

                const handleClearDateRange = () => {
                    if (!category) {
                        return;
                    }

                    dispatch({
                        type: "CLEAR_DATE_RANGE",
                        payload: {
                            filterType: type,
                            parentCategoryId: category.id,
                            categoryId: categoryId,
                        },
                    });
                    onCategoryClear?.(categoryId, type);
                };

                const handleRemoveOption = (optionId: string) => {
                    dispatch({
                        type: "REMOVE_FILTER",
                        payload: {
                            filterType: type,
                            categoryId: categoryId,
                            optionId,
                        },
                    });
                };

                const handleClearNumberRange = () => {
                    if (!category) {
                        return;
                    }

                    dispatch({
                        type: "CLEAR_NUMBER_RANGE",
                        payload: {
                            filterType: type,
                            parentCategoryId: category.id,
                            categoryId: categoryId,
                        },
                    });
                };

                const resolveValueLabel = (value: string): string => {
                    return findFilterOption(validCategories, categoryId, value)?.label?.trim() || value;
                };

                const getChips = () => {
                    if (category?.type === "date-operator" && hasDateOperatorValues(selectedOptions as string[])) {
                        return (
                            <Chip
                                key={`${type}-${categoryId}-date-operator`}
                                label={getDateOperatorDisplayText(selectedOptions as string[])}
                                size="small"
                                onDelete={() => handleClearFilters(categoryId)}
                                deleteIcon={<Close fontSize="small"/>}
                                sx={{m: 0.5}}
                            />
                        );
                    }

                    if (category?.type === "number-range" && hasNumberRangeValues(selectedOptions as string[])) {
                        return (
                            <Chip
                                key={`${type}-${categoryId}-number-range`}
                                label={getNumberRangeDisplayText(
                                    selectedOptions as string[],
                                    category.valueFormat,
                                )}
                                size="small"
                                onDelete={handleClearNumberRange}
                                deleteIcon={<Close fontSize="small"/>}
                                sx={{m: 0.5}}
                            />
                        );
                    }

                    if (isAllSelected) {
                        return (
                            <Chip
                                key={`${type}-${categoryId}-all`}
                                label="All"
                                size="small"
                                onDelete={() => handleClearFilters(categoryId)}
                                deleteIcon={<Close fontSize="small"/>}
                                sx={{m: 0.5}}
                            />
                        );
                    }

                    if (category?.type === "date-range" && selectedOptions.length === 2) {
                        return (
                            <Chip
                                key={`${type}-${categoryId}-date-range`}
                                label={getOptionLabel("date-range", selectedOptions)}
                                size="small"
                                onDelete={handleClearDateRange}
                                deleteIcon={<Close fontSize="small"/>}
                                sx={{m: 0.5}}
                            />
                        );
                    }

                    return Array.isArray(selectedOptions)
                        ? selectedOptions.map((optionId) => (
                            <Chip
                                key={`${type}-${categoryId}-${optionId as string}`}
                                label={resolveValueLabel(optionId as string)}
                                size="small"
                                onDelete={handleRemoveOption.bind(null, optionId as string)}
                                deleteIcon={<Close fontSize="small"/>}
                                sx={{m: 0.5}}
                            />
                        ))
                        : null;
                };

                return (
                  <Box key={`${type}-${categoryId}`} mb={2} ml={4} mr={2}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={type === "exclude" ? 1 : 0}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {categoryLabel}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleClearFilters(categoryId)}
                      >
                        <Delete
                          sx={{ fontSize: "16px", color: "text.secondary" }}
                        />
                      </IconButton>
                    </Box>

                    <Box>{getChips()}</Box>
                  </Box>
                );
            })}
        </>
    );
};
